import {Box, HStack, Text} from '@chakra-ui/react'
import React, {MouseEvent, useEffect, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {isNumber} from 'lodash-es'
import {useTranslation} from 'next-i18next'
import {engraveOptionName} from 'modules/product/utils/engraveOptionName'
import InputWithCounter from '_app/components/Form/InputWithCounter'
import {EngraveTabComponentProps} from '_app/types/product'
import getHTMLInputElementCaretPosition from '_app/utils/getHTMLInputElementCaretPosition'
import {Button} from '_app/components'
import LocalStorageHelper from '../../../../helpers/LocalStorageHelper/LocalStorageHelper'

const SPECIAL_CHARACTERS = ['∞', '♡']

const Handwriting = ({
                       option,
                       buttonProps,
                       onAddOption,
                     }: EngraveTabComponentProps) => {
  const { t } = useTranslation('engraving')
  const [caretPosition, setCaretPosition] = useState<[number, number]>([0, 0])
  const [length, setLength] = useState(0)
  const maxCharacters =
      option?.value?.max_characters && option?.value?.max_characters > 0
          ? option.value.max_characters
          : undefined

  const { required } = option
  const { setValue, getValues } = useFormContext()
  const inputName = `${engraveOptionName(option)}.value`

  const onAddChar = (char: string) => {
    const value: string = getValues(inputName) || ''
    const [start, stop] = caretPosition
    const newValue = `${value.substring(0, start)}${char}${value.substring(
        stop,
        value.length
    )}`

    if (isNumber(maxCharacters) && newValue.length > maxCharacters) {
      return
    }

    setCaretPosition([start + char.length, stop + char.length])
    setValue(inputName, newValue)
    setLength(newValue.length)
  }

  const inputKeyUpHandler = (e: any) => {
    const value = e?.target?.value
    LocalStorageHelper.set('engraving', value)
    const regexRule = /[^\p{Letter}\p{Mark}\p{Number}\p{Punctuation}\p{Sc}\p{Sk}\p{Sm}\s♡∞]+/gu
    const cleanedValue = value.replace(regexRule, '')
    setValue(inputName, cleanedValue)
    setLength(cleanedValue.length)
    const position = getHTMLInputElementCaretPosition(e?.nativeEvent?.target as HTMLInputElement)
    if (!position) return
    setCaretPosition(position)
  }

  const inputClickHandler = (e: MouseEvent<HTMLInputElement, globalThis.MouseEvent>) => {
    const position = getHTMLInputElementCaretPosition(e?.nativeEvent?.target as HTMLInputElement)
    if (!position) return
    setCaretPosition(position)
  }

  useEffect(() => {
    LocalStorageHelper.remove('engraving')
  }, [])

  return (
      <Box>
        <Box
            display="flex"
            flexDirection={{ base: 'column', md: 'row' }}
            justifyContent="space-between"
            gap={{ base: 2, md: 6 }}
            mb={2}
            alignItems="center"
        >
          <InputWithCounter
              placeholder={t('ENGRAVING_CONTENT_EXAMPLE')}
              isRequired={Boolean(required)}
              mb={1}
              maxCharacters={maxCharacters}
              name={inputName}
              label={t('ENGRAVING_CONTENT')}
              onKeyUp={inputKeyUpHandler}
              onClick={inputClickHandler}
              length={length}
              disablePasting
          />
          <Button onClick={onAddOption} width="100%" disabled={getValues(inputName)?.length === 0} {...buttonProps} />
        </Box>
        <HStack spacing="0.375rem" alignItems="center" pb={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'inline-flex' }}>
            <Text fontWeight={300} mr={4}>{t('INSERT_SPECIAL_CHARACTER')}</Text>
            {SPECIAL_CHARACTERS.map(char => (
                <Text
                    key={`spc-${char}`}
                    fontSize="1.2em"
                    role="button"
                    mr={2}
                    onClick={() => onAddChar(char)}
                >
                  {char}
                </Text>
            ))}
          </Box>
          {
              getValues(inputName)?.length > 0 && (
                  <span style={{ color: 'red' }}>{t('ERROR_TO_ACCEPT')}</span>
              )
          }
        </HStack>
      </Box>
  )
}

export default Handwriting
